import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  startCheckout,
  type CheckoutError,
  type CheckoutInstance,
  type CheckoutManifest as EasypayCheckoutManifest,
  type CheckoutOutput,
  type CheckoutPaymentError,
} from '@easypaypt/checkout-sdk';
import type {
  Address,
  CheckoutInitiateInput,
  CheckoutInitiateResponse,
  PaymentMethod,
  PickupPointPublic,
  ShippingCalculationResult,
} from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { formatEur, formatPostalCodePT } from '../../lib/format';
import { useCartStore } from '../../stores/cartStore';
import { useAuthStore } from '../../stores/authStore';
import { api, extractError } from '../../lib/api';
import { useAppSettings } from '../../lib/appSettings';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { ResendVerificationButton } from '../../components/common/ResendVerificationButton';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../hooks/useFormErrors';
import {
  combine,
  email as emailRule,
  nif as nifRule,
  phonePT,
  postalCodePT,
  required as requiredRule,
} from '../../lib/validate';
import styles from './CheckoutPage.module.scss';

type DetailsFieldKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'nif'
  | 'address1'
  | 'postalCode'
  | 'city'
  | 'district'
  | 'billingFirstName'
  | 'billingLastName'
  | 'billingAddress1'
  | 'billingPostalCode'
  | 'billingCity'
  | 'billingDistrict';

type PaymentFieldKey = 'mbwayPhone';

interface ValidateResponse {
  valid: boolean;
  issues: Array<{ variantId: string; message: string }>;
  subtotal: number;
  totalWeightGrams: number;
  shipping: ShippingCalculationResult;
  coupon: { valid: boolean; discountAmount?: number; freeShipping?: boolean; error?: string } | null;
}

type Selection =
  | { kind: 'rate'; rateId: string }
  | { kind: 'pickup'; pickupPointId: string };

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  district: string;
  country: string;
  nif: string;
  notes: string;
  useShippingAsBilling: boolean;
  billingFirstName: string;
  billingLastName: string;
  billingAddress1: string;
  billingAddress2: string;
  billingCity: string;
  billingPostalCode: string;
  billingDistrict: string;
  billingCountry: string;
  paymentMethod: PaymentMethod | '';
  mbwayPhone: string;
}

const initialForm: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  postalCode: '',
  district: '',
  country: 'PT',
  nif: '',
  notes: '',
  useShippingAsBilling: true,
  billingFirstName: '',
  billingLastName: '',
  billingAddress1: '',
  billingAddress2: '',
  billingCity: '',
  billingPostalCode: '',
  billingDistrict: '',
  billingCountry: 'PT',
  paymentMethod: '',
  mbwayPhone: '',
};

type Step = 1 | 2;

type AddressMode =
  | { kind: 'saved'; addressId: string }
  | { kind: 'new' };

export default function CheckoutPage(): JSX.Element {
  const navigate = useNavigate();
  const items = useCartStore((s) => s.items);
  const appliedCouponCode = useCartStore((s) => s.appliedCoupon?.code ?? null);
  const clearCart = useCartStore((s) => s.clearCart);

  const user = useAuthStore((s) => s.user);

  const { enabledPaymentMethods } = useAppSettings();
  const isPaymentEnabled = (method: PaymentMethod): boolean =>
    enabledPaymentMethods.includes(method);

  const [step, setStep] = useState<Step>(1);

  const [form, setForm] = useState<FormState>(() => ({
    ...initialForm,
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    nif: user?.nif ?? '',
  }));

  const [validated, setValidated] = useState<ValidateResponse | null>(null);
  const [pickupPoints, setPickupPoints] = useState<PickupPointPublic[]>([]);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressMode, setAddressMode] = useState<AddressMode>({ kind: 'new' });
  const [billingAddressMode, setBillingAddressMode] = useState<AddressMode>({
    kind: 'new',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [guestChoiceMade, setGuestChoiceMade] = useState(false);
  const detailsErrors = useFormErrors<DetailsFieldKey>();
  const paymentErrors = useFormErrors<PaymentFieldKey>();
  // Set just before clearCart() in handleSubmit so the empty-cart guard below
  // doesn't redirect to /carrinho while we're navigating to the confirmation page.
  const submittedRef = useRef(false);

  // EasyPay Checkout SDK — inline iframe for credit-card payments. Card data
  // never reaches our server. We mount it when the buyer commits to paying
  // by card; until then the container div is just an empty slot.
  const checkoutContainerRef = useRef<HTMLDivElement | null>(null);
  const checkoutInstanceRef = useRef<CheckoutInstance | null>(null);
  interface CardOrderInfo {
    orderNumber: string;
    amount: string;
    manifestId: string;
  }
  const [cardOrder, setCardOrder] = useState<CardOrderInfo | null>(null);
  const [cardSessionLoading, setCardSessionLoading] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  // SDK callbacks are passed once at mount time, so they close over the state
  // they captured then. Mirror cardOrder into a ref so onSuccess can read the
  // current value even if React re-renders between mount and callback.
  const cardOrderRef = useRef<CardOrderInfo | null>(null);
  useEffect(() => {
    cardOrderRef.current = cardOrder;
  }, [cardOrder]);

  // Tear the iframe down if the buyer switches to another method or leaves
  // the page entirely. Also clears the loading flag — a switch can race with
  // an in-flight session-creation request, and we don't want a stale flag
  // to block the next attempt.
  useEffect(() => {
    if (form.paymentMethod !== 'credit_card') {
      checkoutInstanceRef.current?.unmount();
      checkoutInstanceRef.current = null;
      setCardOrder(null);
      setCardError(null);
      setCardSessionLoading(false);
    }
  }, [form.paymentMethod]);
  useEffect(() => {
    return () => {
      checkoutInstanceRef.current?.unmount();
      checkoutInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (items.length === 0 && !submittedRef.current) navigate('/carrinho');
  }, [items.length, navigate]);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      firstName: f.firstName || user.firstName || '',
      lastName: f.lastName || user.lastName || '',
      email: f.email || user.email || '',
      phone: f.phone || user.phone || '',
      nif: f.nif || user.nif || '',
    }));
  }, [user]);

  useEffect(() => {
    if (!user) {
      setAddresses([]);
      setAddressMode({ kind: 'new' });
      setBillingAddressMode({ kind: 'new' });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<Address[]>('/account/addresses');
        if (cancelled) return;
        setAddresses(response.data);
        const preferred =
          response.data.find((a) => a.isDefaultShipping) ?? response.data[0];
        if (preferred) {
          setAddressMode({ kind: 'saved', addressId: preferred.id });
        }
      } catch {
        // Silently ignore — checkout still works with manual entry.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (addressMode.kind !== 'saved') return;
    const picked = addresses.find((a) => a.id === addressMode.addressId);
    if (!picked) return;
    setForm((f) => ({
      ...f,
      firstName: picked.firstName,
      lastName: picked.lastName,
      address1: picked.addressLine1,
      address2: picked.addressLine2 ?? '',
      city: picked.city,
      postalCode: picked.postalCode,
      district: picked.district,
      country: picked.country,
    }));
  }, [addressMode, addresses]);

  const billingPreselectedRef = useRef(false);
  useEffect(() => {
    if (form.useShippingAsBilling) {
      billingPreselectedRef.current = false;
      return;
    }
    if (billingPreselectedRef.current) return;
    if (addresses.length === 0) return;
    const preferred =
      addresses.find((a) => a.isDefaultBilling) ?? addresses[0];
    setBillingAddressMode({ kind: 'saved', addressId: preferred.id });
    billingPreselectedRef.current = true;
  }, [form.useShippingAsBilling, addresses]);

  useEffect(() => {
    if (billingAddressMode.kind !== 'saved') return;
    const picked = addresses.find((a) => a.id === billingAddressMode.addressId);
    if (!picked) return;
    setForm((f) => ({
      ...f,
      billingFirstName: picked.firstName,
      billingLastName: picked.lastName,
      billingAddress1: picked.addressLine1,
      billingAddress2: picked.addressLine2 ?? '',
      billingCity: picked.city,
      billingPostalCode: picked.postalCode,
      billingDistrict: picked.district,
      billingCountry: picked.country,
    }));
  }, [billingAddressMode, addresses]);

  const isPickup = selection?.kind === 'pickup';
  const hasValidPostalCode = Boolean(form.postalCode.match(/^\d{4}-\d{3}$/));
  const canValidate = hasValidPostalCode && items.length > 0;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
    if ((detailsErrors.errors as Record<string, string | undefined>)[key as string]) {
      detailsErrors.clearError(key as DetailsFieldKey);
    }
    if ((paymentErrors.errors as Record<string, string | undefined>)[key as string]) {
      paymentErrors.clearError(key as PaymentFieldKey);
    }
  };

  const validateDetails = (): Partial<Record<DetailsFieldKey, string>> => {
    const next: Partial<Record<DetailsFieldKey, string>> = {};
    const firstNameErr = requiredRule(form.firstName);
    if (firstNameErr) next.firstName = firstNameErr;
    const lastNameErr = requiredRule(form.lastName);
    if (lastNameErr) next.lastName = lastNameErr;
    const emailErr = combine(requiredRule, emailRule)(form.email);
    if (emailErr) next.email = emailErr;
    const phoneErr = combine(requiredRule, phonePT)(form.phone);
    if (phoneErr) next.phone = phoneErr;
    const nifErr = nifRule(form.nif);
    if (nifErr) next.nif = nifErr;

    if (!isPickup && addressMode.kind === 'new') {
      const addr1Err = requiredRule(form.address1);
      if (addr1Err) next.address1 = addr1Err;
      const postalReq = requiredRule(form.postalCode);
      if (postalReq) next.postalCode = postalReq;
      else {
        const postalErr = postalCodePT(form.postalCode);
        if (postalErr) next.postalCode = postalErr;
      }
      const cityErr = requiredRule(form.city);
      if (cityErr) next.city = cityErr;
      const districtErr = requiredRule(form.district);
      if (districtErr) next.district = districtErr;
    }

    if (!form.useShippingAsBilling && billingAddressMode.kind === 'new') {
      const bfn = requiredRule(form.billingFirstName);
      if (bfn) next.billingFirstName = bfn;
      const bln = requiredRule(form.billingLastName);
      if (bln) next.billingLastName = bln;
      const ba1 = requiredRule(form.billingAddress1);
      if (ba1) next.billingAddress1 = ba1;
      const bpcReq = requiredRule(form.billingPostalCode);
      if (bpcReq) next.billingPostalCode = bpcReq;
      else {
        const bpcErr = postalCodePT(form.billingPostalCode);
        if (bpcErr) next.billingPostalCode = bpcErr;
      }
      const bcity = requiredRule(form.billingCity);
      if (bcity) next.billingCity = bcity;
      const bdist = requiredRule(form.billingDistrict);
      if (bdist) next.billingDistrict = bdist;
    }

    return next;
  };

  const validatePayment = (): Partial<Record<PaymentFieldKey, string>> => {
    const next: Partial<Record<PaymentFieldKey, string>> = {};
    if (form.paymentMethod === 'mbway') {
      const mbErr = combine(requiredRule, phonePT)(form.mbwayPhone);
      if (mbErr) next.mbwayPhone = mbErr;
    }
    return next;
  };

  useEffect(() => {
    if (!canValidate) {
      // Postal code no longer valid → drop any cached pickup points so the
      // section disappears until the customer re-enters a valid one.
      setPickupPoints([]);
      setSelection((current) => (current?.kind === 'pickup' ? null : current));
      return;
    }
    const timeout = setTimeout(() => {
      void (async () => {
        try {
          const response = await api.post<ValidateResponse>('/checkout/validate', {
            items: items.map((i) => ({
              productId: i.productId,
              variantId: i.variantId,
              quantity: i.quantity,
            })),
            postalCode: form.postalCode,
            couponCode: appliedCouponCode ?? undefined,
          });
          setValidated(response.data);
          setPickupPoints(response.data.shipping.pickupPoints);
          setSelection((current) => {
            if (current?.kind === 'pickup') return current;
            const firstRate = response.data.shipping.rates[0];
            if (firstRate) return { kind: 'rate', rateId: firstRate.rate.id };
            return null;
          });
        } catch (err) {
          setError(extractError(err, t.checkout.errorShippingFailed));
        }
      })();
    }, 400);
    return () => clearTimeout(timeout);
  }, [canValidate, form.postalCode, items, appliedCouponCode]);

  const selectedRate = useMemo(() => {
    if (!validated || selection?.kind !== 'rate') return null;
    return validated.shipping.rates.find((r) => r.rate.id === selection.rateId) ?? null;
  }, [validated, selection]);

  const selectedPickup = useMemo(() => {
    if (selection?.kind !== 'pickup') return null;
    return pickupPoints.find((p) => p.id === selection.pickupPointId) ?? null;
  }, [pickupPoints, selection]);

  const subtotal = isPickup
    ? items.reduce((sum, i) => sum + i.price * i.quantity, 0)
    : validated?.subtotal ?? 0;
  const shippingCost = isPickup ? 0 : selectedRate?.finalPrice ?? 0;
  const discount = validated?.coupon?.valid ? validated.coupon.discountAmount ?? 0 : 0;
  const total = Math.max(0, subtotal + shippingCost - discount);

  const goToPayment = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    const errs = validateDetails();
    detailsErrors.setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (!selection) {
      setError(t.checkout.errorSelectShippingRate);
      return;
    }
    setStep(2);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildInitiatePayload = (paymentMethod: PaymentMethod): CheckoutInitiateInput | null => {
    if (!selection) return null;
    const basePayload = {
      items: items.map((i) => ({
        productId: i.productId,
        variantId: i.variantId,
        quantity: i.quantity,
      })),
      guest: user
        ? undefined
        : {
            email: form.email,
            phone: form.phone || undefined,
          },
      // Sent for guests and account holders alike: `guest` is undefined once
      // signed in, and the number is asked for on the form either way.
      contactPhone: form.phone || undefined,
      useShippingAsBilling: form.useShippingAsBilling,
      billingAddress: form.useShippingAsBilling
        ? undefined
        : {
            firstName: form.billingFirstName,
            lastName: form.billingLastName,
            addressLine1: form.billingAddress1,
            addressLine2: form.billingAddress2 || null,
            city: form.billingCity,
            postalCode: form.billingPostalCode,
            district: form.billingDistrict,
            country: form.billingCountry,
            isDefaultShipping: false,
            isDefaultBilling: false,
          },
      billingNif: form.nif || undefined,
      couponCode: appliedCouponCode ?? undefined,
      paymentMethod,
      mbwayPhone: paymentMethod === 'mbway' ? form.mbwayPhone : undefined,
      customerNotes: form.notes || undefined,
      acceptTerms: true as const,
    };
    return selection.kind === 'pickup'
      ? {
          ...basePayload,
          pickupPointId: selection.pickupPointId,
          pickupContact: {
            firstName: form.firstName,
            lastName: form.lastName,
          },
        }
      : {
          ...basePayload,
          shippingAddress: {
            firstName: form.firstName,
            lastName: form.lastName,
            addressLine1: form.address1,
            addressLine2: form.address2 || null,
            city: form.city,
            postalCode: form.postalCode,
            district: form.district,
            country: form.country,
            isDefaultShipping: false,
            isDefaultBilling: false,
          },
          shippingRateId: selection.rateId,
        };
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!selection) {
      setError(t.checkout.errorSelectShippingRate);
      return;
    }
    if (!form.paymentMethod) return;
    // CC is driven by the inline EasyPay iframe, not this button — eager
    // mount via useEffect when the buyer ticks terms with CC selected.
    if (form.paymentMethod === 'credit_card') return;
    const errs = validatePayment();
    paymentErrors.setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const payload = buildInitiatePayload(form.paymentMethod);
    if (!payload) return;

    setSubmitting(true);
    try {
      const response = await api.post<CheckoutInitiateResponse>('/checkout/initiate', payload);
      const result = response.data;
      submittedRef.current = true;
      clearCart();
      const search = buildConfirmationSearch(result);
      navigate(`/checkout/confirmacao?${search.toString()}`);
    } catch (err) {
      setError(extractError(err, t.checkout.errorPaymentFailed));
    } finally {
      setSubmitting(false);
    }
  };

  // Eager CC mount: the moment the buyer selects "Cartão de crédito", create
  // the EasyPay session and mount the iframe inline. Terms acceptance moved
  // to implicit consent at pay time (Stripe/Amazon pattern), so the iframe
  // can appear immediately without a checkbox gate. We only fire once per
  // CC selection — switching methods clears cardOrder so re-selecting CC
  // creates a fresh session. Note: cardSessionLoading is deliberately NOT
  // in the dep list — including it would cause the effect to re-run the
  // instant we set it true, cancelling its own in-flight request.
  useEffect(() => {
    if (form.paymentMethod !== 'credit_card') return;
    if (cardOrder || cardSessionLoading || cardError) return;
    if (!selection) return;

    const payload = buildInitiatePayload('credit_card');
    if (!payload) return;

    let cancelled = false;
    setCardSessionLoading(true);
    void (async () => {
      try {
        const response = await api.post<CheckoutInitiateResponse>(
          '/checkout/initiate',
          payload
        );
        if (cancelled) return;
        const result = response.data;
        if (result.paymentMethod !== 'credit_card' || !result.checkoutManifest) {
          setCardError(t.checkout.errorPaymentFailed);
          return;
        }
        setCardOrder({
          orderNumber: result.orderNumber,
          amount: result.amount,
          manifestId: result.checkoutManifest.id,
        });
        mountEasypayCheckout(
          result.checkoutManifest as EasypayCheckoutManifest,
          result.checkoutManifest.testing
        );
      } catch (err) {
        if (cancelled) return;
        setCardError(extractError(err, t.checkout.errorPaymentFailed));
      } finally {
        if (!cancelled) setCardSessionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // buildInitiatePayload reads many form fields; we deliberately only
    // react to the gating signals so we don't re-fire on every keystroke
    // while CC + terms are already active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.paymentMethod, cardOrder, cardError, selection]);

  const buildConfirmationSearch = (result: CheckoutInitiateResponse): URLSearchParams => {
    const search = new URLSearchParams({ order: result.orderNumber });
    if (result.entity) search.set('entity', result.entity);
    if (result.reference) search.set('reference', result.reference);
    if (result.expiresAt) search.set('expires', result.expiresAt);
    if (result.mbwayRequestId) search.set('pid', result.mbwayRequestId);
    if (result.checkoutManifest) search.set('pid', result.checkoutManifest.id);
    search.set('method', result.paymentMethod);
    search.set('amount', result.amount);
    if (selectedPickup) {
      const deadline = new Date();
      deadline.setUTCDate(deadline.getUTCDate() + selectedPickup.daysToCollect);
      search.set('pickup', '1');
      search.set('pickupName', selectedPickup.name);
      search.set('pickupAddress', selectedPickup.address);
      search.set('pickupDeadline', deadline.toISOString());
    }
    return search;
  };

  const mountEasypayCheckout = (manifest: EasypayCheckoutManifest, testing: boolean): void => {
    // Wait one tick so the conditional container div is in the DOM, then hand
    // it to the SDK. The SDK looks up the element by id.
    window.setTimeout(() => {
      if (!checkoutContainerRef.current) return;
      checkoutInstanceRef.current?.unmount();
      checkoutInstanceRef.current = startCheckout(manifest, {
        id: 'easypay-checkout-container',
        display: 'inline',
        testing,
        language: 'pt_PT',
        hideDetails: true,
        onSuccess: handleEasypaySuccess,
        onError: handleEasypayError,
        onPaymentError: handleEasypayPaymentError,
        onClose: handleEasypayClose,
      });
    }, 0);
  };

  const handleEasypaySuccess = (_info: CheckoutOutput): void => {
    if (!cardOrderRef.current) return;
    const order = cardOrderRef.current;
    checkoutInstanceRef.current?.unmount();
    checkoutInstanceRef.current = null;
    submittedRef.current = true;
    clearCart();
    const search = new URLSearchParams({
      order: order.orderNumber,
      method: 'credit_card',
      amount: order.amount,
      pid: order.manifestId,
    });
    if (selectedPickup) {
      const deadline = new Date();
      deadline.setUTCDate(deadline.getUTCDate() + selectedPickup.daysToCollect);
      search.set('pickup', '1');
      search.set('pickupName', selectedPickup.name);
      search.set('pickupAddress', selectedPickup.address);
      search.set('pickupDeadline', deadline.toISOString());
    }
    navigate(`/checkout/confirmacao?${search.toString()}`);
  };

  const handleEasypayError = (err: CheckoutError): void => {
    checkoutInstanceRef.current?.unmount();
    checkoutInstanceRef.current = null;
    setCardOrder(null);
    setCardError(
      err.code === 'already-paid'
        ? t.checkout.cardAlreadyPaid
        : err.code === 'checkout-expired'
        ? t.checkout.cardSessionExpired
        : t.checkout.errorPaymentFailed
    );
  };

  const handleEasypayPaymentError = (_err: CheckoutPaymentError): void => {
    // Recoverable — the SDK keeps the iframe mounted so the buyer can retry.
    setCardError(t.checkout.cardPaymentRetry);
  };

  const handleEasypayClose = (): void => {
    checkoutInstanceRef.current?.unmount();
    checkoutInstanceRef.current = null;
    setCardOrder(null);
  };

  const VAT_RATE = 0.23;
  const vatAmount = total > 0 ? total - total / (1 + VAT_RATE) : 0;

  const shippingLabel = isPickup
    ? t.checkout.pickupInStore
    : selectedRate
    ? `${t.cart.shipping} · ${selectedRate.rate.name}`
    : t.cart.shipping;

  const shippingValueIsFree =
    isPickup || (selectedRate ? selectedRate.isFree : false);
  const shippingValue = !selectedRate && !isPickup
    ? '—'
    : shippingValueIsFree
    ? t.checkout.free
    : formatEur(shippingCost);

  const summary = (
    <aside className={styles.summary}>
      <button
        type="button"
        className={styles.summaryToggle}
        onClick={() => setSummaryOpen((v) => !v)}
        aria-expanded={summaryOpen}
        aria-controls="checkout-summary-body"
      >
        <span>{summaryOpen ? t.checkout.hideSummary : t.checkout.viewSummary}</span>
        <span className={styles.summaryToggleRight}>
          <span className={styles.summaryToggleTotal}>{formatEur(total)}</span>
          <span
            className={`${styles.summaryToggleCaret} ${
              summaryOpen ? styles.summaryToggleCaretOpen : ''
            }`}
            aria-hidden
          />
        </span>
      </button>
      <div
        id="checkout-summary-body"
        className={`${styles.summaryBody} ${
          summaryOpen ? styles.summaryBodyOpen : ''
        }`}
      >
        <h3>{t.checkout.summary}</h3>
        <ul className={styles.items}>
          {items.map((i) => {
            const lineTotal = i.price * i.quantity;
            return (
              <li key={i.variantId} className={styles.item}>
                {i.imageUrl ? (
                  <Link to={`/produtos/${i.slug}`} className={styles.itemThumb}>
                    <img src={i.imageUrl} alt={i.name} loading="lazy" />
                    {i.quantity > 1 ? (
                      <span className={styles.itemQtyBadge}>{i.quantity}</span>
                    ) : null}
                  </Link>
                ) : (
                  <Link
                    to={`/produtos/${i.slug}`}
                    className={`${styles.itemThumb} ${styles.itemThumbFallback}`}
                    aria-label={i.name}
                  >
                    {i.quantity > 1 ? (
                      <span className={styles.itemQtyBadge}>{i.quantity}</span>
                    ) : null}
                  </Link>
                )}
                <div className={styles.itemBody}>
                  <div className={styles.itemRow}>
                    <Link to={`/produtos/${i.slug}`} className={styles.name}>
                      {i.name}
                    </Link>
                    <span className={styles.price}>{formatEur(lineTotal)}</span>
                  </div>
                  <div className={styles.itemMeta}>
                    {i.size} · {i.colour}
                  </div>
                  {i.quantity > 1 ? (
                    <div className={styles.itemUnit}>
                      {i.quantity} × {formatEur(i.price)}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
        <hr className={styles.divider} />
        <div className={styles.totals}>
          <div className={styles.totalRow}>
            <span className={styles.label}>{t.cart.subtotal}</span>
            <span className={styles.value}>{formatEur(subtotal)}</span>
          </div>
          <div className={styles.totalRow}>
            <span className={styles.label}>{shippingLabel}</span>
            <span
              className={`${styles.value} ${
                shippingValueIsFree ? styles.valueFree : ''
              }`}
            >
              {shippingValue}
            </span>
          </div>
          {selectedPickup ? (
            <div className={styles.totalRow}>
              <span className={styles.label}>{t.checkout.pickupOptionAddress}</span>
              <span className={styles.value} style={{ textAlign: 'right' }}>
                {selectedPickup.name}
              </span>
            </div>
          ) : null}
          {discount > 0 ? (
            <div className={styles.totalRow}>
              <span className={styles.label}>
                {t.cart.discount}
                {appliedCouponCode ? (
                  <span className={styles.couponChip}>{appliedCouponCode}</span>
                ) : null}
              </span>
              <span className={`${styles.value} ${styles.valueDiscount}`}>
                −{formatEur(discount)}
              </span>
            </div>
          ) : null}
        </div>
        <hr className={styles.divider} />
        <div className={styles.totals}>
          <div className={`${styles.totalRow} ${styles.grand}`}>
            <span className={styles.label}>{t.cart.total}</span>
            <span className={styles.value}>{formatEur(total)}</span>
          </div>
          {vatAmount > 0 ? (
            <div className={styles.vatLine}>
              {t.checkout.vatLineLabel.replace('{rate}', String(VAT_RATE * 100))}{' '}
              {formatEur(vatAmount)}
            </div>
          ) : null}
        </div>
        <div className={styles.trust}>
          <span className={styles.trustItem}>
            <svg
              className={styles.trustIcon}
              viewBox="0 0 16 16"
              aria-hidden
              focusable="false"
            >
              <path
                fill="currentColor"
                d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7H3.5A1.5 1.5 0 0 0 2 8.5v5A1.5 1.5 0 0 0 3.5 15h9a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 12.5 7h-1V4.5A3.5 3.5 0 0 0 8 1Zm-2 6V4.5a2 2 0 1 1 4 0V7H6Z"
              />
            </svg>
            {t.checkout.secureNotice}
          </span>
          <span className={styles.trustItem}>
            <svg
              className={styles.trustIcon}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              focusable="false"
            >
              <path d="M2.5 8a5.5 5.5 0 1 0 1.65-3.92" />
              <path d="M2 2.5v3h3" />
            </svg>
            {t.checkout.returnsNotice}
          </span>
        </div>
        <div className={styles.paymentStrip}>
          <span className={styles.paymentStripLabel}>
            {t.checkout.paymentMethodsLabel}
          </span>
          <span className={styles.paymentStripLogos}>
            {isPaymentEnabled('mbway') ? (
              <img src="/payment-methods/mbway.png" alt="MB WAY" />
            ) : null}
            {isPaymentEnabled('multibanco') ? (
              <img src="/payment-methods/mb.png" alt="Multibanco" />
            ) : null}
            {isPaymentEnabled('credit_card') ? (
              <img src="/payment-methods/cc.png" alt="Visa, Mastercard, Maestro" />
            ) : null}
          </span>
        </div>
      </div>
    </aside>
  );

  if (!user && !guestChoiceMade) {
    return (
      <>
        <SEO title={t.checkout.title} noindex />
        <div className={`container ${styles.wrapper}`}>
          <h1 className={styles.title}>{t.checkout.title}</h1>
          <section className={styles.gate}>
            <h2 className={styles.gateTitle}>{t.checkout.guestGateTitle}</h2>
            <p className={styles.gateLead}>{t.checkout.guestGateLead}</p>
            <div className={styles.gateOptions}>
              <button
                type="button"
                className={styles.gateOption}
                onClick={() =>
                  navigate('/conta/login', { state: { from: '/checkout' } })
                }
              >
                <strong>{t.checkout.guestGateLogin}</strong>
                <small>{t.checkout.guestGateLoginHint}</small>
              </button>
              <button
                type="button"
                className={styles.gateOption}
                onClick={() => setGuestChoiceMade(true)}
              >
                <strong>{t.checkout.guestGateGuest}</strong>
                <small>{t.checkout.guestGateGuestHint}</small>
              </button>
            </div>
            <p className={styles.gateRegister}>
              {t.checkout.guestGateRegister}{' '}
              <Link to="/conta/registo" state={{ from: '/checkout' }}>
                {t.checkout.guestGateRegisterCta}
              </Link>
            </p>
          </section>
        </div>
      </>
    );
  }

  if (user && !user.emailVerified) {
    return (
      <>
        <SEO title={t.checkout.title} noindex />
        <div className={`container ${styles.wrapper}`}>
          <h1 className={styles.title}>{t.checkout.title}</h1>
          <section className={styles.gate}>
            <h2 className={styles.gateTitle}>{t.auth.verifyCheckoutTitle}</h2>
            <p className={styles.gateLead}>{t.auth.verifyCheckoutLead}</p>
            <div className={styles.verifyActions}>
              <ResendVerificationButton
                variant="primary"
                size="lg"
                messageClassName={styles.verifyMessage}
              />
            </div>
          </section>
        </div>
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        <SEO title={`${t.checkout.pageTitleStep2} — ${t.checkout.title}`} noindex />
        <form className={`container ${styles.wrapper}`} onSubmit={handleSubmit} noValidate>
          <h1 className={styles.title}>{t.checkout.pageTitleStep2}</h1>
          <div className={styles.grid}>
          <div className={styles.form}>
            <div className={styles.paymentStep}>
              <button
                type="button"
                className={styles.backLink}
                onClick={() => {
                  setError(null);
                  setStep(1);
                  if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                ← {t.checkout.backToDetails}
              </button>

              <p className={styles.paymentIntro}>{t.checkout.choosePaymentMethod}</p>

              <div className={styles.payments}>
                {!enabledPaymentMethods.length ? (
                  <p className={styles.error}>{t.checkout.noPaymentMethods}</p>
                ) : null}
                {isPaymentEnabled('mbway') ? (
                <div className={styles.paymentCard}>
                  <label className={styles.paymentHeader}>
                    <span className={styles.paymentLeft}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="mbway"
                        checked={form.paymentMethod === 'mbway'}
                        onChange={() => setField('paymentMethod', 'mbway')}
                      />
                      {t.checkout.mbway}
                    </span>
                    <span className={styles.paymentLogos}>
                      <img src="/payment-methods/mbway.png" alt="MB WAY" />
                    </span>
                  </label>
                  {form.paymentMethod === 'mbway' ? (
                    <div className={styles.paymentBody}>
                      <Field
                        label={t.checkout.mbwayPhone}
                        required
                        error={paymentErrors.errors.mbwayPhone}
                      >
                        <PhoneInput
                          value={form.mbwayPhone}
                          onChange={(v) => setField('mbwayPhone', v)}
                          invalid={Boolean(paymentErrors.errors.mbwayPhone)}
                        />
                      </Field>
                    </div>
                  ) : null}
                </div>
                ) : null}

                {isPaymentEnabled('multibanco') ? (
                <div className={styles.paymentCard}>
                  <label className={styles.paymentHeader}>
                    <span className={styles.paymentLeft}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="multibanco"
                        checked={form.paymentMethod === 'multibanco'}
                        onChange={() => setField('paymentMethod', 'multibanco')}
                      />
                      Multibanco
                    </span>
                    <span className={styles.paymentLogos}>
                      <img src="/payment-methods/mb.png" alt="Multibanco" />
                    </span>
                  </label>
                </div>
                ) : null}

                {isPaymentEnabled('credit_card') ? (
                <div className={styles.paymentCard}>
                  <label className={styles.paymentHeader}>
                    <span className={styles.paymentLeft}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="credit_card"
                        checked={form.paymentMethod === 'credit_card'}
                        onChange={() => setField('paymentMethod', 'credit_card')}
                      />
                      {t.checkout.creditCard}
                    </span>
                    <span className={styles.paymentLogos}>
                      <img src="/payment-methods/cc.png" alt="Visa, Mastercard, Maestro" />
                    </span>
                  </label>
                  {form.paymentMethod === 'credit_card' ? (
                    <div className={styles.paymentBody}>
                      {cardError ? (
                        <p className={styles.fieldError} role="alert">
                          {cardError}
                        </p>
                      ) : cardSessionLoading && !cardOrder ? (
                        <p className={styles.cardRedirectNote}>
                          {t.checkout.cardSessionLoading}
                        </p>
                      ) : null}
                      <div
                        id="easypay-checkout-container"
                        ref={checkoutContainerRef}
                        className={styles.easypayContainer}
                      />
                      <p className={styles.implicitTerms}>
                        {t.checkout.implicitTermsPrefix}{' '}
                        <Link to="/termos-e-condicoes">
                          {t.checkout.implicitTermsLinks.terms}
                        </Link>
                        {t.checkout.implicitTermsJoin}
                        <Link to="/politica-de-privacidade">
                          {t.checkout.implicitTermsLinks.privacy}
                        </Link>
                        .
                      </p>
                    </div>
                  ) : null}
                </div>
                ) : null}
              </div>

              {error ? <p className={styles.error}>{error}</p> : null}

              {form.paymentMethod === 'credit_card' ? null : (
                <>
                  <button
                    type="submit"
                    className={styles.submit}
                    disabled={submitting || !form.paymentMethod}
                  >
                    {submitting ? t.common.loading : t.checkout.payNow}
                  </button>
                  <p className={styles.implicitTerms}>
                    {t.checkout.implicitTermsPrefix}{' '}
                    <Link to="/termos-e-condicoes">
                      {t.checkout.implicitTermsLinks.terms}
                    </Link>
                    {t.checkout.implicitTermsJoin}
                    <Link to="/politica-de-privacidade">
                      {t.checkout.implicitTermsLinks.privacy}
                    </Link>
                    .
                  </p>
                </>
              )}
            </div>
          </div>

          {summary}
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <SEO title={t.checkout.title} noindex />
      <form className={`container ${styles.wrapper}`} onSubmit={goToPayment} noValidate>
        <h1 className={styles.title}>{t.checkout.title}</h1>
        <div className={styles.grid}>
        <div className={styles.form}>
          <fieldset>
            <legend>{t.checkout.steps.details}</legend>
            <div className={styles.row}>
              <Field label={t.checkout.firstName} required error={detailsErrors.errors.firstName}>
                <input
                  value={form.firstName}
                  onChange={(e) => setField('firstName', e.target.value)}
                />
              </Field>
              <Field label={t.checkout.lastName} required error={detailsErrors.errors.lastName}>
                <input
                  value={form.lastName}
                  onChange={(e) => setField('lastName', e.target.value)}
                />
              </Field>
            </div>
            <Field label={t.checkout.email} required error={detailsErrors.errors.email}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                disabled={Boolean(user)}
              />
            </Field>
            <Field label={t.checkout.phone} required error={detailsErrors.errors.phone}>
              <PhoneInput
                value={form.phone}
                onChange={(v) => setField('phone', v)}
                invalid={Boolean(detailsErrors.errors.phone)}
              />
            </Field>
            <Field label={t.checkout.nif} error={detailsErrors.errors.nif}>
              <input
                value={form.nif}
                onChange={(e) => setField('nif', e.target.value)}
                maxLength={9}
              />
            </Field>
          </fieldset>

          <fieldset>
            <legend>{t.checkout.steps.shipping}</legend>
            {!isPickup && user && addresses.length > 0 ? (
              <div className={styles.addressOptions}>
                <h4 className={styles.addressOptionsTitle}>
                  {t.checkout.savedAddressesTitle}
                </h4>
                {addresses.map((a) => (
                  <label key={a.id} className={styles.addressOption}>
                    <input
                      type="radio"
                      name="savedAddress"
                      value={a.id}
                      checked={
                        addressMode.kind === 'saved' &&
                        addressMode.addressId === a.id
                      }
                      onChange={() =>
                        setAddressMode({ kind: 'saved', addressId: a.id })
                      }
                    />
                    <span className={styles.addressInfo}>
                      <strong>
                        {a.label ?? `${a.firstName} ${a.lastName}`}
                        {a.isDefaultShipping ? (
                          <span className={styles.addressBadge}>
                            {t.checkout.savedAddressDefaultBadge}
                          </span>
                        ) : null}
                      </strong>
                      <small>
                        {a.addressLine1}
                        {a.addressLine2 ? `, ${a.addressLine2}` : ''}
                      </small>
                      <small>
                        {a.postalCode} {a.city}
                      </small>
                    </span>
                  </label>
                ))}
                <label className={styles.addressOption}>
                  <input
                    type="radio"
                    name="savedAddress"
                    value="__new__"
                    checked={addressMode.kind === 'new'}
                    onChange={() => {
                      setAddressMode({ kind: 'new' });
                      setForm((f) => ({
                        ...f,
                        address1: '',
                        address2: '',
                        city: '',
                        postalCode: '',
                        district: '',
                      }));
                    }}
                  />
                  <span className={styles.addressInfo}>
                    <strong>{t.checkout.newAddressOption}</strong>
                  </span>
                </label>
              </div>
            ) : null}

            {!isPickup && addressMode.kind === 'new' ? (
              <>
                <Field
                  label={t.checkout.address1}
                  required
                  error={detailsErrors.errors.address1}
                >
                  <input
                    value={form.address1}
                    onChange={(e) => setField('address1', e.target.value)}
                  />
                </Field>
                <Field label={t.checkout.address2}>
                  <input
                    value={form.address2}
                    onChange={(e) => setField('address2', e.target.value)}
                  />
                </Field>
                <div className={styles.row}>
                  <Field
                    label={t.checkout.postalCode}
                    required
                    error={detailsErrors.errors.postalCode}
                  >
                    <input
                      value={form.postalCode}
                      onChange={(e) => setField('postalCode', formatPostalCodePT(e.target.value))}
                      placeholder="0000-000"
                      inputMode="numeric"
                      maxLength={8}
                    />
                  </Field>
                  <Field label={t.checkout.city} required error={detailsErrors.errors.city}>
                    <input
                      value={form.city}
                      onChange={(e) => setField('city', e.target.value)}
                    />
                  </Field>
                  <Field
                    label={t.checkout.district}
                    required
                    error={detailsErrors.errors.district}
                  >
                    <input
                      value={form.district}
                      onChange={(e) => setField('district', e.target.value)}
                    />
                  </Field>
                </div>
              </>
            ) : null}

            <div className={styles.rates}>
              <h4>{t.checkout.shippingMethodTitle}</h4>

              {pickupPoints.length > 0 ? (
                <div className={styles.rateGroup}>
                  <h5 className={styles.rateGroupHeading}>
                    {t.checkout.pickupGroupTitle}
                  </h5>
                  {pickupPoints.map((p) => (
                    <label
                      key={p.id}
                      className={`${styles.rateOption} ${styles.rateOptionPickup}`}
                    >
                      <input
                        type="radio"
                        name="shippingRate"
                        value={`pickup:${p.id}`}
                        checked={selection?.kind === 'pickup' && selection.pickupPointId === p.id}
                        onChange={() => setSelection({ kind: 'pickup', pickupPointId: p.id })}
                      />
                      <span className={styles.rateInfo}>
                        <strong>{p.name}</strong>
                        <small>{p.address}</small>
                        <small>
                          {t.checkout.pickupDeadlineDays.replace(
                            '{days}',
                            String(p.daysToCollect)
                          )}
                        </small>
                        <span className={styles.ratePrice}>{t.checkout.free}</span>
                      </span>
                      {p.imageThumbUrl ? (
                        <img
                          src={p.imageThumbUrl}
                          alt={p.name}
                          className={styles.pickupThumb}
                        />
                      ) : (
                        <span className={styles.pickupThumbFallback} aria-hidden />
                      )}
                    </label>
                  ))}
                </div>
              ) : null}

              {validated && validated.shipping.rates.length > 0 ? (
                <div className={styles.rateGroup}>
                  <h5 className={styles.rateGroupHeading}>
                    {t.checkout.deliveryGroupTitle}
                  </h5>
                  {validated.shipping.rates.map(({ rate, finalPrice, isFree }) => (
                    <label key={rate.id} className={styles.rateOption}>
                      <input
                        type="radio"
                        name="shippingRate"
                        value={rate.id}
                        checked={selection?.kind === 'rate' && selection.rateId === rate.id}
                        onChange={() => setSelection({ kind: 'rate', rateId: rate.id })}
                      />
                      <span className={styles.rateInfo}>
                        <strong>{rate.name}</strong>
                        {rate.estimatedDays ? <small>{rate.estimatedDays}</small> : null}
                        <span className={styles.ratePrice}>
                          {isFree ? t.checkout.free : formatEur(finalPrice)}
                        </span>
                      </span>
                      <img
                        src="/shipping/ctt-logo.png"
                        alt="CTT"
                        className={styles.carrierLogo}
                      />
                    </label>
                  ))}
                </div>
              ) : null}

              {validated && validated.shipping.rates.length === 0 && pickupPoints.length === 0 ? (
                <p className={styles.error}>{t.checkout.shippingNoRates}</p>
              ) : null}
              {!validated ? (
                <p className={styles.muted}>{t.checkout.shippingHint}</p>
              ) : null}
            </div>
          </fieldset>

          <fieldset>
            <legend>{t.checkout.billingTitle}</legend>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={!form.useShippingAsBilling}
                onChange={(e) =>
                  setField('useShippingAsBilling', !e.target.checked)
                }
              />
              {t.checkout.billingDifferentToggle}
            </label>

            {!form.useShippingAsBilling && user && addresses.length > 0 ? (
              <div className={styles.addressOptions}>
                <h4 className={styles.addressOptionsTitle}>
                  {t.checkout.billingSavedTitle}
                </h4>
                {addresses.map((a) => (
                  <label key={a.id} className={styles.addressOption}>
                    <input
                      type="radio"
                      name="billingSavedAddress"
                      value={a.id}
                      checked={
                        billingAddressMode.kind === 'saved' &&
                        billingAddressMode.addressId === a.id
                      }
                      onChange={() =>
                        setBillingAddressMode({
                          kind: 'saved',
                          addressId: a.id,
                        })
                      }
                    />
                    <span className={styles.addressInfo}>
                      <strong>
                        {a.label ?? `${a.firstName} ${a.lastName}`}
                        {a.isDefaultBilling ? (
                          <span className={styles.addressBadge}>
                            {t.checkout.savedAddressDefaultBadge}
                          </span>
                        ) : null}
                      </strong>
                      <small>
                        {a.addressLine1}
                        {a.addressLine2 ? `, ${a.addressLine2}` : ''}
                      </small>
                      <small>
                        {a.postalCode} {a.city}
                      </small>
                    </span>
                  </label>
                ))}
                <label className={styles.addressOption}>
                  <input
                    type="radio"
                    name="billingSavedAddress"
                    value="__new__"
                    checked={billingAddressMode.kind === 'new'}
                    onChange={() => {
                      setBillingAddressMode({ kind: 'new' });
                      setForm((f) => ({
                        ...f,
                        billingFirstName: '',
                        billingLastName: '',
                        billingAddress1: '',
                        billingAddress2: '',
                        billingCity: '',
                        billingPostalCode: '',
                        billingDistrict: '',
                      }));
                    }}
                  />
                  <span className={styles.addressInfo}>
                    <strong>{t.checkout.newAddressOption}</strong>
                  </span>
                </label>
              </div>
            ) : null}

            {!form.useShippingAsBilling && billingAddressMode.kind === 'new' ? (
              <>
                <div className={styles.row}>
                  <Field
                    label={t.checkout.firstName}
                    required
                    error={detailsErrors.errors.billingFirstName}
                  >
                    <input
                      value={form.billingFirstName}
                      onChange={(e) =>
                        setField('billingFirstName', e.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label={t.checkout.lastName}
                    required
                    error={detailsErrors.errors.billingLastName}
                  >
                    <input
                      value={form.billingLastName}
                      onChange={(e) =>
                        setField('billingLastName', e.target.value)
                      }
                    />
                  </Field>
                </div>
                <Field
                  label={t.checkout.address1}
                  required
                  error={detailsErrors.errors.billingAddress1}
                >
                  <input
                    value={form.billingAddress1}
                    onChange={(e) =>
                      setField('billingAddress1', e.target.value)
                    }
                  />
                </Field>
                <Field label={t.checkout.address2}>
                  <input
                    value={form.billingAddress2}
                    onChange={(e) =>
                      setField('billingAddress2', e.target.value)
                    }
                  />
                </Field>
                <div className={styles.row}>
                  <Field
                    label={t.checkout.postalCode}
                    required
                    error={detailsErrors.errors.billingPostalCode}
                  >
                    <input
                      value={form.billingPostalCode}
                      onChange={(e) =>
                        setField(
                          'billingPostalCode',
                          formatPostalCodePT(e.target.value),
                        )
                      }
                      placeholder="0000-000"
                      inputMode="numeric"
                      maxLength={8}
                    />
                  </Field>
                  <Field
                    label={t.checkout.city}
                    required
                    error={detailsErrors.errors.billingCity}
                  >
                    <input
                      value={form.billingCity}
                      onChange={(e) => setField('billingCity', e.target.value)}
                    />
                  </Field>
                  <Field
                    label={t.checkout.district}
                    required
                    error={detailsErrors.errors.billingDistrict}
                  >
                    <input
                      value={form.billingDistrict}
                      onChange={(e) =>
                        setField('billingDistrict', e.target.value)
                      }
                    />
                  </Field>
                </div>
              </>
            ) : null}
          </fieldset>

          <fieldset>
            <Field label={t.checkout.customerNotes}>
              <textarea
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                rows={3}
              />
            </Field>
          </fieldset>

          {error ? <p className={styles.error}>{error}</p> : null}

          <button type="submit" className={styles.submit} disabled={!selection}>
            {t.checkout.continueToPayment}
          </button>
        </div>

        {summary}
        </div>
      </form>
    </>
  );
}
